import { feedbackRead } from "components/Analytics/feedbackRead";
import React, { useEffect, useState } from "react";
import { Alert, Button, Input, Spinner } from "reactstrap";
import { reasonLabel } from "./FeedbackRatingsModal";

const TOP_CARDS = 4;
const TOP_FLAGS = 6;
// On unless turned off, and ratings only store flags set on the user, so their
// "off" side is mostly users who have it on.
const DEFAULT_ON_FLAGS = ["get_all_notes", "user_note_rules", "show_multi_recorder"];

const pct = (value) => (value == null ? "—" : `${Math.round(value)}%`);
const count = (value) => Number(value || 0).toLocaleString();
const pts = (value) => `${value > 0 ? "+" : value < 0 ? "−" : ""}${Math.abs(Math.round(value))} pts`;

function Bar({ value, tone = "fb-bar-teal" }) {
  return <span className={`fb-bar ${tone}`} aria-hidden="true"><span style={{ width: `${Math.min(100, value || 0)}%` }} /></span>;
}

function ReasonLine({ index, reason }) {
  return (
    <li>
      <div className="fb-compare-reason">
        <span>{index != null ? `${index + 1}. ` : ""}{reasonLabel(reason.reason)}</span>
        <strong>{pct(reason.pct)}</strong>
      </div>
      {!!reason.sections.length && <div className="fb-compare-where">
        mostly in {reason.sections.map((s) => <span className="fb-where-chip" key={s.section}>{s.section} {pct(s.pct)}</span>)}
      </div>}
    </li>
  );
}

// Show the busiest cards; "Show all" reveals the rest with a search box, since
// note types and EMR hosts are open-ended.
function CardList({ items, render, label }) {
  const [showAll, setShowAll] = useState(false);
  const [search, setSearch] = useState("");
  const q = search.trim().toLowerCase();
  const shown = showAll ? items.filter((item) => !q || item.value.toLowerCase().includes(q)) : items.slice(0, TOP_CARDS);
  return (
    <>
      {showAll && <Input type="search" className="fb-section-search" placeholder={`Search ${items.length} ${label}`}
        aria-label={`Search ${label}`} value={search} onChange={(event) => setSearch(event.target.value)} />}
      <div className="fb-compare-grid">{shown.map(render)}</div>
      {!shown.length && <p className="fb-muted">No {label} match.</p>}
      {items.length > TOP_CARDS && (
        <button type="button" className="fb-show-all" onClick={() => { setShowAll(!showAll); setSearch(""); }}>
          {showAll ? `Show the top ${TOP_CARDS}` : `Show all ${items.length} ${label}`}
        </button>
      )}
    </>
  );
}

// Section 04: visit types (the EMR's own note-type labels), EMRs, and features
// side by side. Clicking a card filters the whole dashboard to it.
export default function FeedbackCompare({ apiClient, request, noteType, emr, onFilterNoteType, onFilterEmr }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  const [allFlags, setAllFlags] = useState(false);

  useEffect(() => {
    let active = true;
    setError("");
    setData(null);
    feedbackRead(apiClient, "/v1/notes/feedback/compare", request, () => active).then((response) => {
      if (!active) return;
      if (response?.error) throw new Error(response.error);
      // Missing lists become empty, so a partial response can't take the page down.
      setData({
        ...response,
        noteTypes: response?.noteTypes || [],
        emrs: response?.emrs || [],
        flags: response?.flags || [],
        multiRecord: response?.multiRecord || null,
      });
    }).catch(() => { if (active) setError("Unable to load the comparisons. Try again."); });
    return () => { active = false; };
  }, [apiClient, request, retry]);

  if (error) return <Alert color="danger">{error} <Button size="sm" onClick={() => setRetry(retry + 1)}>Retry</Button></Alert>;
  if (!data) return <div className="fb-loading"><Spinner size="sm" /> Loading comparisons…</div>;

  const flags = allFlags ? data.flags : data.flags.slice(0, TOP_FLAGS);
  const defaultOnShown = flags.some((f) => DEFAULT_ON_FLAGS.includes(f.flag));

  return (
    <>
      <div className="note-intel-panel fb-panel">
        <div className="note-intel-panel-header">
          <div>
            <h3>How each visit type is doing</h3>
            <p>Thumbs-down rate, top 3 reasons and where they show up, by the note type each EMR reports. Click a card to filter the whole dashboard to it.</p>
          </div>
        </div>
        {data.noteTypes.length
          ? <CardList items={data.noteTypes} label="visit types" render={(card) => (
              <button type="button" key={card.value} aria-pressed={noteType === card.value}
                className={`fb-compare-card fb-clickable${noteType === card.value ? " fb-selected" : ""}`}
                onClick={() => onFilterNoteType(noteType === card.value ? "" : card.value)}>
                <span className="fb-compare-title">{card.value} ›</span>
                <span className="fb-compare-big">{pct(card.downRate)}</span>
                <Bar value={card.downRate} />
                <span className="fb-muted">{count(card.rated)} rated · {count(card.downs)} thumbs downs</span>
                {!!card.reasons.length && <>
                  <span className="fb-compare-label">Top {card.reasons.length} {card.reasons.length === 1 ? "reason" : "reasons"} → where</span>
                  <ul className="fb-compare-reasons">{card.reasons.map((r, i) => <ReasonLine key={r.reason} index={i} reason={r} />)}</ul>
                </>}
              </button>
            )} />
          : <p className="fb-muted">No ratings in these filters yet.</p>}
      </div>

      <div className="note-intel-panel fb-panel fb-emr-panel">
        <div className="note-intel-panel-header">
          <div>
            <h3>How each EMR is doing</h3>
            <p>Compared to the average across all EMRs ({pct(data.averageDownRate)}). Click a card to filter the whole dashboard to it.</p>
          </div>
        </div>
        {data.emrs.length
          ? <CardList items={data.emrs} label="EMRs" render={(card) => {
              const tone = card.vsAverage == null || Math.abs(card.vsAverage) < 3 ? "avg" : card.vsAverage > 0 ? "worse" : "better";
              return (
                <button type="button" key={card.value} aria-pressed={emr === card.value}
                  className={`fb-compare-card fb-clickable fb-emr-${tone}${emr === card.value ? " fb-selected" : ""}`}
                  onClick={() => onFilterEmr(emr === card.value ? "" : card.value)}>
                  <span className="fb-compare-head">
                    <span className="fb-compare-title">{card.value} ›</span>
                    <span className={`fb-vs fb-vs-${tone}`}>
                      {tone === "avg" ? "About average" : `${pts(card.vsAverage)} vs avg`}
                    </span>
                  </span>
                  <span className="fb-emr-rate"><span className="fb-compare-big">{pct(card.downRate)}</span> <span className="fb-muted">thumbs-down rate · avg {pct(data.averageDownRate)}</span></span>
                  <span className="fb-compare-pills">
                    <span className="fb-pill fb-pill-strong">{count(card.users)} active {card.users === 1 ? "user" : "users"}</span>
                    <span className="fb-pill">{pct(card.usersPct)} of all users</span>
                  </span>
                  <span className="fb-muted">{count(card.rated)} rated notes · {count(card.downs)} thumbs downs</span>
                  {card.downsPerWeek != null && <span className="fb-emr-weekly">≈{card.downsPerWeek} thumbs downs a week</span>}
                  {card.smallGroup && <span className="fb-small-group">Small group, numbers can swing a lot</span>}
                  {!!card.reasons.length && <>
                    <span className="fb-compare-label">#1 reason → where</span>
                    <ul className="fb-compare-reasons"><ReasonLine reason={card.reasons[0]} /></ul>
                  </>}
                  <span className="fb-compare-label">Worse than average at</span>
                  {card.worseAt.length
                    ? <ul className="fb-worse-list">{card.worseAt.map((item) => (
                        <li key={`${item.kind}:${item.name}`}>
                          {item.kind === "reason" ? reasonLabel(item.name) : `${item.name} section`} <strong>{pts(item.points)}</strong>
                        </li>
                      ))}</ul>
                    : <span className="fb-good">Nothing stands out</span>}
                </button>
              );
            }} />
          : <p className="fb-muted">No ratings in these filters yet.</p>}
      </div>

      <div className="note-intel-panel fb-panel">
        <div className="note-intel-panel-header">
          <div>
            <h3>Do our features help?</h3>
            <p>Thumbs-down rate with it on vs off. Lower is better. Flags with at least 20 ratings on each side, biggest gap first.</p>
          </div>
        </div>
        <div className="fb-feature-grid">
          {data.multiRecord && (
            <div className="fb-feature">
              <strong>Multi record</strong>
              <div className="fb-feature-row"><span>Multi record</span><Bar value={data.multiRecord.multi.rate} /><b>{pct(data.multiRecord.multi.rate)}</b></div>
              <div className="fb-feature-row"><span>Single record</span><Bar value={data.multiRecord.single.rate} tone="fb-bar-gray" /><b>{pct(data.multiRecord.single.rate)}</b></div>
              <span className="fb-muted">{count(data.multiRecord.multi.rated)} vs {count(data.multiRecord.single.rated)} rated</span>
            </div>
          )}
          {flags.map((flag) => (
            <div className="fb-feature" key={flag.flag}>
              <strong>{flag.flag}{DEFAULT_ON_FLAGS.includes(flag.flag) ? " *" : ""}</strong>
              <div className="fb-feature-row"><span>On</span><Bar value={flag.on.rate} /><b>{pct(flag.on.rate)}</b></div>
              <div className="fb-feature-row"><span>Off</span><Bar value={flag.off.rate} tone="fb-bar-gray" /><b>{pct(flag.off.rate)}</b></div>
              <span className="fb-muted">{count(flag.on.rated)} vs {count(flag.off.rated)} rated</span>
            </div>
          ))}
        </div>
        {!data.flags.length && !data.multiRecord && <p className="fb-muted">Not enough ratings on both sides of any feature yet.</p>}
        {data.flags.length > TOP_FLAGS && (
          <button type="button" className="fb-show-all" onClick={() => setAllFlags(!allFlags)}>
            {allFlags ? `Show the top ${TOP_FLAGS}` : `Show all ${data.flags.length} features`}
          </button>
        )}
        {data.multiRecord?.headsUp && (
          <p className="fb-heads-up">
            <strong>Heads up:</strong> “{reasonLabel(data.multiRecord.headsUp.reason)}” in {data.multiRecord.headsUp.section} is{" "}
            <strong>{data.multiRecord.headsUp.ratio}×</strong> as common on multi record visits. Could be transcripts getting mixed between patients.
          </p>
        )}
        <p className="fb-footnote">
          On/off groups aren't random: clinicians who have a feature on may already differ from those who don't, so treat gaps as hints, not proof.
          {defaultOnShown && " * On by default and only saved when set on a user, so \"off\" is mostly users who have it on."}
        </p>
      </div>
    </>
  );
}
